import { preloadKakaoShare } from '../../kakao-share';
import { createShareImageFile } from '../share-image';
import type { ChatReport } from '../types';
import { trackKrc } from './analytics';
import { copyText, requireElement, type MountedScreen } from './dom';
import { TOOL_URL, type UiEnvironment } from './environment';
import { actionsMarkup, awardsMarkup, coverMarkup, linesMarkup, notesMarkup, sampleBannerMarkup, subjectsMarkup } from './result-sections';
import { saveReportImage } from './save-dialog';
import { statCardsMarkup } from './stat-cards';
import type { Toast } from './toast';
import { kakaoShareToast, shareToolOnKakao } from './tool-share';

/**
 * Result screen (spec §6.2-4): cover, subjects, awards, top remarks, actions,
 * statistic cards and accuracy notes. The share image is rendered as soon as
 * the screen opens so the save tap can call the native share directly.
 */

export interface ResultScreenOptions {
  readonly report: ChatReport;
  readonly environment: UiEnvironment;
  readonly toast: Toast;
  readonly isSample: boolean;
  readonly onRestart: () => void;
  readonly onEditNames: () => void;
}

export function mountResultScreen(stage: HTMLElement, options: ResultScreenOptions): MountedScreen {
  const { report, toast } = options;
  const cards = statCardsMarkup(report.stats);
  const section = document.createElement('section');
  section.className = 'krc-screen krc-result';
  section.setAttribute('aria-labelledby', 'krc-result-title');
  section.innerHTML = `
    ${options.isSample ? sampleBannerMarkup() : ''}
    ${coverMarkup(report, options.isSample)}
    ${subjectsMarkup(report)}
    ${awardsMarkup(report)}
    <div class="krc-result-pair">${linesMarkup(report)}${actionsMarkup()}</div>
    <section class="krc-section" aria-labelledby="krc-details-title">
      <div class="krc-section-head"><h2 id="krc-details-title">자세히 보기</h2><p>성적표 뒤에 숨은 숫자들이에요.</p></div>
      <div class="krc-stats-wide">${cards.wide}</div>
      <div class="krc-stats-columns">${cards.columns}</div>
    </section>
    ${notesMarkup(report.stats)}
    <div class="krc-result-end"><button class="button button-soft" type="button" data-action="restart">다른 대화 분석하기</button></div>`;
  stage.append(section);

  let disposed = false;
  let imageFile: File | null = null;
  let preparing: Promise<File | null> | null = null;
  const saveButton = requireElement<HTMLButtonElement>(section, '[data-action="save"]');
  const saveLabel = requireElement<HTMLElement>(saveButton, '[data-label]');
  const kakaoButton = requireElement<HTMLButtonElement>(section, '[data-action="kakao"]');
  const kakaoLabel = requireElement<HTMLElement>(kakaoButton, '[data-label]');
  const context = { environment: options.environment, toast };

  const prepareImage = (): Promise<File | null> => {
    if (imageFile) return Promise.resolve(imageFile);
    if (preparing) return preparing;
    saveButton.disabled = true;
    saveLabel.textContent = '이미지 준비 중…';
    preparing = createShareImageFile(report).then(
      (file) => {
        imageFile = file;
        return file;
      },
      (error: unknown) => {
        console.error(error);
        if (!disposed) toast('이미지를 준비하지 못했어요. 다시 눌러 주세요.');
        return null;
      },
    ).finally(() => {
      preparing = null;
      if (disposed) return;
      saveButton.disabled = false;
      saveLabel.textContent = '성적표 이미지 저장';
    });
    return preparing;
  };

  saveButton.addEventListener('click', async () => {
    if (!imageFile) {
      // The native share must run inside a tap, so a late image needs a second tap.
      const file = await prepareImage();
      if (file && !disposed) toast('이미지가 준비됐어요. 한 번 더 눌러 저장해 주세요.');
      return;
    }
    saveButton.disabled = true;
    try {
      const mode = await saveReportImage(imageFile, saveButton, context);
      if (mode !== 'cancelled') trackKrc('krc_image_saved', { mode });
      if (mode === 'download') toast('이미지 다운로드를 요청했어요. 다운로드 목록을 확인해 주세요.');
      else if (mode === 'shared') toast('공유 메뉴를 닫았어요. 저장한 위치를 확인해 주세요.');
    } catch (error) {
      console.error(error);
      toast('이미지를 저장하지 못했어요. 잠시 후 다시 시도해 주세요.');
    } finally {
      if (!disposed) saveButton.disabled = false;
    }
  });

  kakaoButton.addEventListener('click', async () => {
    kakaoButton.disabled = true;
    try {
      const mode = await shareToolOnKakao();
      if (mode === 'kakao') trackKrc('krc_kakao_shared', null);
      if (mode === 'kakao-ready') kakaoLabel.textContent = '준비 완료 · 카카오톡 열기';
      else kakaoLabel.textContent = '카카오톡으로 알려주기';
      toast(kakaoShareToast(mode));
    } finally {
      kakaoButton.disabled = false;
    }
  });

  section.addEventListener('click', async (event) => {
    const action = event.target instanceof Element ? event.target.closest<HTMLElement>('[data-action]')?.dataset.action : undefined;
    if (action === 'restart') options.onRestart();
    else if (action === 'edit') options.onEditNames();
    else if (action === 'copy-link') {
      toast(await copyText(TOOL_URL) ? '카톡 성적표 링크를 복사했어요.' : '링크를 복사하지 못했어요. 주소창의 주소를 복사해 주세요.');
    }
  });

  // Start at the latest weeks of the calendar.
  requestAnimationFrame(() => {
    section.querySelectorAll<HTMLElement>('.krc-heat-scroll').forEach((scroller) => {
      scroller.scrollLeft = scroller.scrollWidth;
    });
  });
  preloadKakaoShare();
  void prepareImage();
  trackKrc('krc_report_viewed', { room_kind: report.stats.roomKind });

  return {
    heading: requireElement(section, '#krc-result-title'),
    dispose() {
      disposed = true;
      section.remove();
    },
  };
}
